/**
 * slideTransformTemplateApi.ts — client for the TEMPLATE-BASED slide-transform
 * mode, the sibling of `slideTransformApi.ts`'s freehand redesign.
 *
 * Three calls, in the order the flow makes them:
 *
 *   1. POST {base}/slides/transform-retrieve       → ranked template candidates
 *   2. GET  {base}/slides/transform-template-file  → that template's `.pptx`
 *   3. POST {base}/slides/transform-template       → a mapping plan per slide
 *
 * Step 1 also caches the source slide's content inventory server-side under
 * `request_id`, which is why step 3 takes the SAME `request_id` and does not
 * resend the original slide — call them as a pair, and only once per retrieve
 * (the server releases the cache when /transform-template returns, so a replay
 * fails with `NO_CACHED_CONTENT`).
 *
 * Paths start at the segment AFTER `config.deckorateApiBaseUrl`, which already
 * carries the service's `/v1/deckorate` prefix — see the note in
 * `slideTransformApi.ts`. Wire shapes mirror
 * `DeckorateAPI/models/slide_transform/slide_transform_template.py` one-for-one.
 *
 * Auth is not handled here: callers pass the shared `AuthorizedFetch` from
 * `useDeckorateHttp()`.
 */

import type { AuthorizedFetch } from "../../../services/deckorateApi";
import type { SlideDimensions } from "./slideTransformApi";

const RETRIEVE_PATH = "/slides/transform-retrieve";
const TRANSFORM_PATH = "/slides/transform-template";
const TEMPLATE_FILE_PATH = "/slides/transform-template-file";

/* ── Errors ─────────────────────────────────────────────────────── */

/** Raised for any HTTP-level failure from the three endpoints above. */
export class TemplateTransformApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly httpStatus?: number,
  ) {
    super(message);
    this.name = "TemplateTransformApiError";
  }
}

/* ── /transform-retrieve ────────────────────────────────────────── */

export interface TemplateRetrieveRequest {
  request_id: string;
  /** The whole source slide XML to classify and match templates against. */
  _xml: string;
  slide_dimensions?: SlideDimensions;
  /** Narrows which catalog folders are searched. Server defaults when omitted. */
  folders?: string[];
  top_n?: number;
}

/** One ranked template. The list arrives already sorted best-first. */
export interface TemplateCandidate {
  id: string;
  folder?: string | null;
  pptxFile?: string | null;
  /** 1-based slide number within the template's `.pptx`. */
  slideIndex?: number | null;
  layoutClass?: string | null;
  qualityTier?: string | null;
  intentTags?: string[];
  /**
   * Match confidence in 0..1 — the ranker's weighted composite. Shown as a
   * percentage in the picker. Absent for a directly-resolved `template_id`,
   * which was never ranked, so treat "no score" as "unknown", not "zero".
   */
  score?: number | null;
}

export interface TemplateRetrieveResponse {
  request_id: string;
  candidates: TemplateCandidate[];
}

/* ── /transform-template ────────────────────────────────────────── */

/**
 * How freely the fitter may restructure the content.
 *
 * `"preserve"` is for a template the ranker matched to this slide — stay close
 * to the original wording. `"generic"` is for one the user picked by browsing,
 * where the layout was chosen for its look and the content may be re-shaped to
 * suit it. Facts are preserved either way. Surfaced in the picker as
 * "Use original" and "AI rewrite".
 */
export type TemplateFitMode = "preserve" | "generic";

export interface TemplateTransformSlideInput {
  /** 0-based index of this slide WITHIN THE BATCH, not in the deck. */
  slide_index: number;
  /** The template slide's raw XML. */
  _xml: string;
  slide_dimensions?: SlideDimensions;
  template_id: string;
  fit_mode?: TemplateFitMode;
}

export interface TemplateTransformRequest {
  /** Must match the `request_id` of the preceding /transform-retrieve call. */
  request_id: string;
  slides: TemplateTransformSlideInput[];
}

/** One emphasis run within a rewritten paragraph. */
export interface MappingRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

export interface MappingRichBullet {
  runs?: MappingRun[];
  hasBullet?: boolean;
}

/** What the fitter decided for one slot. */
export interface PlaceholderAssignment {
  /** Where the text came from, e.g. `"title"`, `"block[0].bullets[1]"`. */
  sourceRef?: string;
  action?: "copy" | "rewrite" | "split" | "leave_blank";
  /** Text to write. `"split"` uses `\n` to separate paragraphs. */
  text?: string;
  /** Per-paragraph runs carrying bold/italic/underline, when emphasis applies. */
  richBullets?: MappingRichBullet[];
}

/**
 * Which shape a plan key belongs to.
 *
 * Plan keys are `"<role>#<index>"` (`"body#1"`), assigned server-side after it
 * classifies each shape's role and sorts them into reading order — nothing in
 * the key itself identifies a shape. The server reports the mapping here so the
 * client does not have to re-implement that classifier; `shapeId` is the
 * shape's OOXML id (`<p:cNvPr id="…">`), unique within the slide.
 */
export interface TemplateSlotRef {
  id: string;
  shapeId?: number | null;
  role?: string | null;
  name?: string | null;
}

export interface TemplateTransformResult {
  request_id: string;
  slide_index: number;
  template_id: string;
  placeholderAssignments: Record<string, PlaceholderAssignment>;
  slots?: TemplateSlotRef[];
  droppedSourceRefs?: string[];
  capacityViolations?: Array<Record<string, unknown>>;
  rationale?: string;
  /** False when the fitter's output could not be parsed — check before applying. */
  valid: boolean;
  retried?: boolean;
  error?: string | null;
}

export interface TemplateTransformResponse {
  request_id: string;
  /** One result per requested slide, in the same order. */
  results: TemplateTransformResult[];
}

/* ── Shared plumbing ────────────────────────────────────────────── */

export interface TemplateApiOptions {
  /** DeckorateAPI base URL (`config.deckorateApiBaseUrl`). */
  readonly baseUrl: string;
  readonly authorizedFetch: AuthorizedFetch;
  readonly signal?: AbortSignal;
}

function buildUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/$/, "")}${path}`;
}

/** Parse the shared `{ error: { code, message } }` envelope off a failed response. */
async function throwForHttpError(res: Response, fallbackMessage: string): Promise<never> {
  let code = `HTTP_${res.status}`;
  let message = `${fallbackMessage} (${res.status})`;
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    if (body?.error?.code) code = body.error.code;
    if (body?.error?.message) message = body.error.message;
  } catch {
    /* non-JSON error body — keep the status-derived defaults */
  }
  throw new TemplateTransformApiError(code, message, res.status);
}

async function postJson<T>(
  path: string,
  body: unknown,
  options: TemplateApiOptions,
  fallbackMessage: string,
): Promise<T> {
  const res = await options.authorizedFetch(buildUrl(options.baseUrl, path), {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (!res.ok) await throwForHttpError(res, fallbackMessage);

  // A 200 that isn't JSON means the request never reached DeckorateAPI — most
  // often an unset base URL resolving against the add-in's own host, which
  // returns the SPA shell. Reported as such rather than as a parse crash.
  try {
    return (await res.json()) as T;
  } catch {
    throw new TemplateTransformApiError(
      "INVALID_RESPONSE",
      "The Deckorate API returned an unexpected response. Check that it is configured and reachable.",
      res.status,
    );
  }
}

/* ── Calls ──────────────────────────────────────────────────────── */

/**
 * Rank catalog templates against the source slide, and cache that slide's
 * content inventory under `request.request_id` for the matching
 * {@link transformIntoTemplates} call.
 */
export async function retrieveTemplates(
  request: TemplateRetrieveRequest,
  options: TemplateApiOptions,
): Promise<TemplateRetrieveResponse> {
  return postJson<TemplateRetrieveResponse>(
    RETRIEVE_PATH,
    request,
    options,
    "Couldn't find matching templates",
  );
}

/**
 * Fit the cached source content into each supplied template slide.
 *
 * `request.request_id` must be the one used for the preceding retrieve, and
 * this call consumes that cache — so it can be made only once per retrieve.
 */
export async function transformIntoTemplates(
  request: TemplateTransformRequest,
  options: TemplateApiOptions,
): Promise<TemplateTransformResponse> {
  return postJson<TemplateTransformResponse>(
    TRANSFORM_PATH,
    request,
    options,
    "Couldn't fit your content into the template",
  );
}

/**
 * The `.pptx` a catalog id lives in, i.e. the id without its `#<slide>` suffix.
 *
 * Catalog ids are `"<folder>/<file>.pptx#<1-based slide>"`, so many layouts
 * share one file — and those files are large (measured against the live
 * catalog: 40 files, mean 13 MB, up to 39.7 MB, because each carries its
 * masters, themes and media). Callers fetching several picks must fetch each
 * distinct file once rather than once per pick; without that, three picks from
 * one file download the same ~24 MB three times.
 *
 * The server resolves the file from the id the same way and ignores the
 * suffix, so any id sharing this key returns identical bytes.
 */
export function templateFileKey(templateId: string): string {
  return templateId.split("#", 1)[0] ?? templateId;
}

/**
 * Download a template's `.pptx` package.
 *
 * The bytes are the raw OPC package: unzip it, take the slide named by the
 * candidate's `slideIndex`, and build the slide to insert from it — see
 * `templatePackage.ts`.
 */
export async function fetchTemplateFile(
  templateId: string,
  options: TemplateApiOptions,
): Promise<ArrayBuffer> {
  const url = `${buildUrl(options.baseUrl, TEMPLATE_FILE_PATH)}?template_id=${encodeURIComponent(templateId)}`;
  const res = await options.authorizedFetch(url, {
    method: "GET",
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (!res.ok) await throwForHttpError(res, "Couldn't download the template");

  const bytes = await res.arrayBuffer();
  // An empty body would surface much later as an opaque Office.js
  // InvalidArgument from insertSlidesFromBase64, so it's caught here instead.
  if (bytes.byteLength === 0) {
    throw new TemplateTransformApiError("TEMPLATE_EMPTY", "The template file was empty.", res.status);
  }
  return bytes;
}
